import PollTransport from "support/transport/PollTransport";

// One normalized event stream, whatever is underneath. The UI never branches on
// transport: it subscribes here and reacts to "messages" and "state", so the
// websocket rung can arrive or disappear without touching a single view.
//
// SEC-04 ladder, two rungs: Reverb websocket if the server says ws_enabled,
// otherwise a poll of /sync. The websocket is dark for the pilot
// (BROADCAST_DRIVER=null), so poll is the complete product and the socket is a
// pure enhancement that may never land.
export default class TransportManager {
  constructor(config) {
    this.config = config || {};
    this.listeners = { messages: [], state: [] };
    this.state = "idle";
    this.active = null;
    this.echo = null;
    this.stopped = true;
  }

  on(event, handler) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(handler);
    return () => {
      this.listeners[event] = this.listeners[event].filter((fn) => fn !== handler);
    };
  }

  emit(event, payload) {
    (this.listeners[event] || []).forEach((fn) => fn(payload));
  }

  setState(state) {
    if (this.state === state) {
      return;
    }
    this.state = state;
    this.emit("state", state);
  }

  async start(uuid, sinceId) {
    this.stopped = false;
    this.uuid = uuid;

    const transport = (this.config && this.config.transport) || {};

    if (transport.ws_enabled && transport.ws_host && transport.ws_key) {
      this.setState("connecting");
      // Only fetched when the server actually offers a socket, so a poll-only
      // deployment never downloads laravel-echo or pusher-js.
      try {
        const module = await import("support/transport/EchoTransport");
        if (this.stopped) {
          return;
        }
        const EchoTransport = module.default;
        this.echo = new EchoTransport(transport, {
          onMessages: (messages) => this.emit("messages", messages),
          onState: (state) => this.setState(state),
          onFallback: () => this.startPolling(uuid, sinceId),
        });
        await this.echo.connect(uuid);
        this.active = "ws";
        return;
      } catch (error) {
        // A socket that cannot even be loaded is not worth reporting to the
        // player; the poll rung below is the product.
      }
    }

    this.startPolling(uuid, sinceId);
  }

  startPolling(uuid, sinceId) {
    if (this.stopped || this.poll) {
      return;
    }
    const transport = (this.config && this.config.transport) || {};
    this.active = "poll";
    this.poll = new PollTransport({
      uuid,
      sinceId: sinceId || 0,
      intervalMs: transport.poll_interval_ms || 12000,
      onMessages: (messages) => this.emit("messages", messages),
      onState: (state) => this.setState(state),
    });
    this.poll.start();
  }

  // The player just sent something, so do not sit out the rest of the interval.
  nudge() {
    if (this.poll) {
      this.poll.tickNow();
    }
  }

  stop() {
    this.stopped = true;
    if (this.poll) {
      this.poll.stop();
      this.poll = null;
    }
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
    }
    this.active = null;
    this.setState("idle");
  }
}
